/**
 * PACKAGE
 * LOCALIZATION
 * CONSTANTS
 * UTILITY
 * STORE
 * HOC COMPONENTS
 * COMPONENTS
 * ASSETS
 * HELP
 * STYLES
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import clsx from 'clsx';
import emoji from 'node-emoji';
import { InputAdornment } from '@material-ui/core';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { LOCATIONS_SEARCH_BAR, LOCATIONS_HOME_PARTNER_PAGE } from '@/constants/locations';
import {
  ROUTE_DISCOVER_SEARCH,
  ROUTE_DISCOVER_APP,
  ROUTE_SHOWS_AND_MOVIES_MOVIE,
  ROUTE_SHOWS_AND_MOVIES_SHOW,
  ROUTE_DISCOVER_CHANNEL,
  ROUTE_SEARCH,
} from '@/constants/routes';
import { HOME_PAGE } from '@/constants/swimlanes';

// UTILITY
import store from '@/utility/store';
import { getLink, routeEnhance } from '@/utility/routes';
import { analyticsInternalLinks, analyticsPageLoaded } from '@/utility/analytics';
import { getS3ImageUrl } from '@/utility/content';

// STORE
import {
  DiscoverActions,
  PagesActions,
  HomeActions,
  OnboardingActions,
  AppDetailsActions,
  SwimlanesActions,
} from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import PageWithFilters from '@/hoc/PageWithFilters';
import withMediaQuery from '@/hoc/withMediaQuery';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import Swimlanes from '@/components/Swimlanes';
import ContentHero from '@/components/Heroes/ContentHero';
import SearchAutoComplete from '@/components/Form/SearchAutoComplete';
import PartnerInternetOffer from '@/components/PartnerInternetOffer';
import Welcome from '@/components/Welcome';

// STYLES
import { Wrapper } from './styles';

const defaultOGImg = getS3ImageUrl('/og-image/og-default.png');

class PartnerHomeAuth extends PageWithFilters {
  constructor(props) {
    super(props);

    this.state = {
      search: '',
    };
    this.debouncedSearch = debounce(search => {
      return props.getDiscoverSearch({
        search_string: search.trim(),
        partner: props.partner?.partner_slug || '',
      });
    }, 700);
  }

  componentDidMount() {
    const { getPagesContent, getPartnerHomeAuthContent } = this.props;

    getPagesContent('homepage,home,home2,share');

    getPartnerHomeAuthContent();

    window.prerendercloudReady = false;
  }

  componentDidUpdate(prevProps) {
    const { isAuth, getPartnerHomeAuthContent } = this.props;

    if (prevProps.isAuth !== isAuth) {
      getPartnerHomeAuthContent();
    }
  }

  componentWillUnmount() {
    const { swimlanesClear } = this.props;

    if (store.get('show_reg_fmb')) {
      store.set('show_reg_fmb', false);
    }

    swimlanesClear();
  }

  onChangeSearch = search => {
    this.setState({ search });
  };

  handleSearch = (event, value, reason) => {
    if (event?.keyCode === 13 && reason === 'reset') {
      return;
    }

    if (reason === 'input' || reason === 'clear') {
      this.onChangeSearch(value);
      this.debouncedSearch(value);
    }
  };

  handleSelect = (event, value, reason, handleOpen) => {
    const {
      router: { navigate },
    } = this.props;

    if (reason === 'clear') {
      this.onChangeSearch('');
      return;
    }

    if (reason === 'select-option') {
      let routeEnhanced;
      const options = {
        state: {
          location: LOCATIONS_SEARCH_BAR,
        },
      };

      switch (value.type) {
        case 'app':
          routeEnhanced = routeEnhance(getLink(ROUTE_DISCOVER_APP, { slug: value.slug }), options);

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'show':
        case 'movie':
          if (value.type === 'show') {
            routeEnhanced = routeEnhance(
              getLink(ROUTE_SHOWS_AND_MOVIES_SHOW, { show_id: value.slug || value.id }),
              options,
            );
          } else if (value.type === 'movie') {
            routeEnhanced = routeEnhance(
              getLink(ROUTE_SHOWS_AND_MOVIES_MOVIE, { movie_id: value.slug || value.id }),
              options,
            );
          }

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);

          break;
        case 'channel':
          routeEnhanced = routeEnhance(
            getLink(ROUTE_DISCOVER_CHANNEL, {
              slug: value.slug || value.id,
            }),
            options,
          );

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'keyword':
          routeEnhanced = routeEnhance(
            `${ROUTE_DISCOVER_SEARCH}?keywords=${value.id}&page=1&type=paid,free`,
            options,
          );

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'genre':
          routeEnhanced = routeEnhance(
            `${ROUTE_DISCOVER_SEARCH}?genres=${value.slug || value.title}&page=1&type=paid,free`,
            options,
          );

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'link':
          routeEnhanced = routeEnhance(value.link, options);

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: value.link,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          if (value.link) window.open(value.link, '_self');
          break;
        case 'show more enter':
          routeEnhanced = routeEnhance(`${ROUTE_SEARCH}?search=${value.value}`, options);

          navigate(routeEnhanced);
          break;
        default:
          break;
      }

      handleOpen(false);
    }
  };

  handleSearchClear = () => {
    const { search } = this.state;
    const { getDiscoverSearch, partner } = this.props;

    if (search) {
      this.setState({ search: '' }, () =>
        getDiscoverSearch({
          search_string: '',
          partner: partner?.partner_slug || '',
        }),
      );
    }
  };

  handleUpdateAppStatus = (data, status) => {
    const { appListUpdateAppStatus } = this.props;

    appListUpdateAppStatus(data.id, status, data);
  };

  handleOpenOnboarding = () => {
    const { onboardingSetState, appListUpdateAppStatuses, getPartnerHomeAuthContent } = this.props;

    onboardingSetState(true, {
      callback: data => {
        appListUpdateAppStatuses(data.apps);

        getPartnerHomeAuthContent();
      },
    });
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;

      window.prerendercloudReady = true;
    }
  };

  render() {
    const { search } = this.state;
    const {
      t,
      content,
      isAuth,
      partner,
      partnerLoading,
      partnerSettings,
      searchData,
      searchLoading,
      searchPrevString,
      section1,
      section1Loadings,
      section2,
      section2Loadings,
      section3,
      section3Loading,
    } = this.props;

    const isPartner = !!partner.slug;
    const searchComp = (
      <SearchAutoComplete
        inputValue={search}
        freeSolo={!search}
        onInputChange={this.handleSearch}
        onChange={this.handleSelect}
        options={search ? searchData : []}
        loading={searchLoading}
        openOnFocus
        searchPrevString={searchPrevString}
        searchPlaceholder={t('inputs.findSuggestionsPlaceholder')}
        TextFieldProps={{
          className: 'search',
          variant: 'filled',
          InputProps: {
            onBlur: this.handleSearchClear,
            startAdornment: (
              <InputAdornment className="search__icon" position="start">
                <i className="icon-search" />
              </InputAdornment>
            ),
          },
        }}
        analyticsLocation={LOCATIONS_HOME_PARTNER_PAGE}
      />
    );

    const bannerAddAppsProps = {
      title: t('homeAuth.banners.onboarding.title'),
      subtitle: t('homeAuth.banners.onboarding.subtitle'),
      btnText: t('buttons.addApps'),
      btnProps: {
        onClick: this.handleOpenOnboarding,
      },
    };

    const seoData = {
      canonicalUrl: siteUrl,
      ogUrl: isPartner ? `${siteUrl}/${partner.slug}` : siteUrl,
      title:
        partner?.meta?.title ||
        content['page-title-home'] ||
        'Find Your Streaming Service - MyBundle',
      ogTitle:
        partner?.meta?.title ||
        content['meta-home-og-title'] ||
        'MyBundle – Streaming TV advice for YOUR needs',
      description:
        partner?.meta?.description ||
        content['shmeta-home-og-description'] ||
        "Stop wasting money on the TV you don't watch.",
      image: partner?.meta?.image || defaultOGImg,
    };

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet onChangeClientState={this.handleChangeHelmetData}>
            <title>{seoData.title}</title>
            <meta property="og:title" content={seoData.ogTitle} />
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:description" content={seoData.description} />
            <meta name="description" content={seoData.description} />
            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content="608514030003288" />
            <meta property="og:image" content={seoData.image} />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta
              name="twitter:title"
              content={partner?.meta?.title || 'MyBundle - Personalized Streaming TV Advice'}
            />
            <meta name="twitter:description" content={seoData.description} />
            <meta name="twitter:image" content={seoData.image} />
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
          </Helmet>
        )}
        <Wrapper className="home-auth">
          {isAuth ? (
            <ContentHero
              className="home-auth__content-hero"
              title={
                <>
                  {partner?.text_overwrites?.partner_registred_title ||
                    t('partnerHomeAuth.hero.title')}{' '}
                  <i>{emoji.get('tv')}</i>
                </>
              }
              subtitle={
                partner?.text_overwrites?.partner_registred_subtitle ||
                t('partnerHomeAuth.hero.subtitle')
              }
              hideBreadcrumbs
            >
              <div className="filters">{searchComp}</div>
            </ContentHero>
          ) : (
            <Welcome />
          )}
          <section className={clsx('home-auth__content', !isAuth && 'home-auth__content--with-bg')}>
            <div className="page-content">
              {!!partnerSettings.search_movies_shows && !isAuth && (
                <div className="home-auth__search">{searchComp}</div>
              )}
              {isAuth && <PartnerInternetOffer partnerBundleSlugs={['epb']} />}

              <Swimlanes
                data={section1}
                loadings={section1Loadings}
                loadingRows={2}
                analyticsLocation={LOCATIONS_HOME_PARTNER_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
                bannerAddAppsProps={bannerAddAppsProps}
              />
              <Swimlanes
                data={section2}
                loadings={section2Loadings}
                loadingRows={2}
                analyticsLocation={LOCATIONS_HOME_PARTNER_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
                swimlanesShift={section1 ? Object.keys(section1).length : 0}
                bannerAddAppsProps={bannerAddAppsProps}
              />
              <Swimlanes
                data={section3}
                loadings={section3Loading}
                loadingRows={2}
                analyticsLocation={LOCATIONS_HOME_PARTNER_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
                swimlanesShift={
                  (section1 ? Object.keys(section1).length : 0) +
                  (section2 ? Object.keys(section2).length : 0)
                }
                bannerAddAppsProps={bannerAddAppsProps}
              />
            </div>
          </section>
        </Wrapper>
      </Layout>
    );
  }
}

PartnerHomeAuth.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  matchesSmDown: PropTypes.bool.isRequired,
  content: PropTypes.object.isRequired,
  contentLoading: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  partnerSettings: PropTypes.object.isRequired,
  getPagesContent: PropTypes.func.isRequired,
  getPartnerHomeAuthContent: PropTypes.func.isRequired,
  swimlanesClear: PropTypes.func.isRequired,
  getDiscoverSearch: PropTypes.func.isRequired,
  searchData: PropTypes.array.isRequired,
  searchLoading: PropTypes.bool.isRequired,
  searchPrevString: PropTypes.string.isRequired,
  onboardingSetState: PropTypes.func.isRequired,
  appListUpdateAppStatus: PropTypes.func.isRequired,
  appListUpdateAppStatuses: PropTypes.func.isRequired,
  section1: PropTypes.object,
  section1Loadings: PropTypes.object.isRequired,
  section2: PropTypes.object,
  section2Loadings: PropTypes.object.isRequired,
  section3: PropTypes.object.isRequired,
  section3Loading: PropTypes.object.isRequired,
};

const mapStateToProps = ({ pages, auth, partner, swimlanes, discover }) => ({
  content: pages.content,
  contentLoading: pages.loading,
  isAuth: auth.isAuth,
  partner: partner.data,
  partnerSettings: partner.data?.settings || {},
  partnerLoading: partner.loadings.partner,
  searchData: discover.search.data,
  searchLoading: discover.loadings.search,
  searchPrevString: discover.search.string,
  section1: swimlanes.section1[HOME_PAGE] || {},
  section1Loadings: swimlanes.loadings.section1[HOME_PAGE] || {},
  section2: swimlanes.section2[HOME_PAGE] || {},
  section2Loadings: swimlanes.loadings.section2[HOME_PAGE] || {},
  section3: swimlanes.section3[HOME_PAGE] || {},
  section3Loading: swimlanes.loadings.section3[HOME_PAGE] || {},
});

const mapDispatchToProps = dispatch => {
  return {
    getPagesContent: pageSlug => dispatch(PagesActions.getPagesContent.action({ pageSlug })),
    getPartnerHomeAuthContent: () => dispatch(HomeActions.getPartnerHomeAuthContent.action()),
    swimlanesClear: () => dispatch(SwimlanesActions.swimlanesClear.action()),
    getDiscoverSearch: params => dispatch(DiscoverActions.getDiscoverSearch.action({ params })),
    onboardingSetState: (state, props) => {
      dispatch(OnboardingActions.onboardingSetState.action({ state, props }));
    },
    appListUpdateAppStatus: (id, status, data) => {
      dispatch(AppDetailsActions.appListUpdateAppStatus.action({ id, status, data }));
    },
    appListUpdateAppStatuses: apps => {
      dispatch(AppDetailsActions.appListUpdateAppStatuses.action({ apps }));
    },
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withMediaQuery({ matchesSmDown: theme => theme.breakpoints.down('sm') }),
  withTranslation(),
  withRouter,
)(PartnerHomeAuth);
